/**
 * JavaScript for the ticketing purchase form page.
 *
 * Due to Google reCaptcha integration, this object is created manually inside this file (rather than being created by the loader)
 * and the instance exposed on the 'window' object. It needs to be accessible globally in the reCaptcha callback function.
 *
 * There are two different Stripe integrations used on this page, each one has a slightly different flow through
 * the code.
 * 1. Card Element, this is used for card purchases
 * 2. Express Checkout Element, this is used for single PayPal/Google Pay/Apple pay purchases
 */

import AnalyticsEvent        from 'Scripts/common/analytics-event';
import AutoPopulateAddress   from 'Scripts/donate-form/auto-populate-address';
import FeesSlider            from 'Scripts/donate-form/fees-slider';
import PaymentMethodUtils    from 'Scripts/donate-form/payment-method-utils';
import RecaptchaUtils        from 'Scripts/donate-form/recaptcha-utils';
import showErrorBar          from 'Scripts/common/show-error-bar';
import Steps                 from 'Scripts/donate-form/steps';
import StripeCardElements    from 'Scripts/donate-form/stripe-card-elements';
import StripeExpressElements from 'Scripts/donate-form/stripe-express-elements';
import stripeStyles          from 'Scripts/donate-form/stripe-styles';
import StripeUtils           from 'Scripts/donate-form/stripe-utils-new';
import TicketBasket          from 'Scripts/donate/ticket-basket';

import { enableTomSelects, enableLoqate, enablePostcodeLabelToggle } from 'Scripts/donate-form/address-utils';
import { disableSubmittingFormWithEnterKey, updateAmountText, togglePhoneInput } from 'Scripts/donate-form/utils';
import { enableParsleyPostcodeValidator, enableParsleyBlocklistValidator, enableParsleyNotEmailValidator, enableParsleyNotHtmlValidator, scrollToParsleyError } from 'Scripts/common/parsley-utils';

// Steps
const STEP_DETAILS = 'details';
const STEP_REVIEW = 'review';
const STEP_PAYMENT_METHOD = 'payment-method';
const STEP_CARD_PAYMENT = 'card-payment';

export default class TicketingForm {
	constructor() {
		console.log("TicketingForm.constructor");

		// Constants
		this.fees = Number($("#feePercentage").val());
		this.tipPerc = Number($("#tipPercentage").val());

		// If lots of errors then we disable Stripe elements
		this.errorCount = 0;

		// Holds a reference to Stripe's API object
		this.stripe = null;

		// Stripe shared utilites, shared between different pages
		this.stripeUtils = null;

		// Holds a reference to Stripe's card number field
		this.cardElements = null;

		// Holds a reference to StripeExpressElements, which manages Stripe's ExpressElements
		this.expressElements = null;

		this.verificationThreshold = parseInt($(document.body).data('verificationThreshold'), 10);

		// Define all the UI elements
		this.selectElements();

		// Manages stepping between screens
		this.steps = new Steps();

		// Set up the ticket basket
		this.basket = new TicketBasket({
			onUpdateBasket: this.onUpdateBasket.bind(this),
			onTimerReset: this.onTimerReset.bind(this),
		});

		this.feesSlider = new FeesSlider({
			amountField: this.ui.amountField,
			onSetFeePercentage: tipPercentage => this.setTip(tipPercentage, null),
			onSetCustomAmount: tipAmount => this.setTip(null, tipAmount),
		});

		this.setupAddressUtils();

		// Manages enabling/disabling certain payment methods
		this.paymentMethods = new PaymentMethodUtils();

		this.setupParsley();
		this.bindEventHandlers();
		this.initStripe();
		this.updateDonationTotal();
	}

	//
	// Setup functions
	//

	selectElements() {
		this.ui = {
			// Containers
			buttonNext: $('.js-next-step'),
			buttonPrev: $('.js-prev-step'),
			// Hidden fields
			feeField: $("#feePercentage"),
			feeThresholds: $(".js-fee-threshold"),
			tipField: $("#tipPercentage"),
			tipAmountField: $("#tipAmount"),
			paymentMethodIdField: $('#sessionId'), // PaymentMethod ID gets saved as session_id on the DB
			paymentIntentIdField: $('#trackingId'), // PaymentIntent ID gets saved as trackingid on the DB
			paymentMethodField: $('#paymentMethod'),
			// Amount screen
			amountField: $('#amount'),
			numTicketsField: $('#tickets'),
			coverFeesField: $('#coverFees'),
			numTicketsText: $(".js-tickets"),
			// Details screen
			addressContainer: $(".js-giftaid-address-fields-container"),
			// Review screen
			reviewNextContainer: $(".js-review-nav--next"),
			reviewCompleteContainer: $(".js-review-nav--complete"),
			feeAmount: $('.js-fee-amount'),
			tipAmount: $('.js-tip-amount'),
			tipPerc: $('.js-tip-perc'),
			// Payment method screen
			paymentMethodStep: $('#js-step-payment-method'),
			paymentMethodSpinner: $('#js-payment-method-spinner'),
			// Card payment screen
			billingCountry: $('#billing-country'),
			billingStateText: $('.billing-state-text'),
			billingState: $('#billing-state'),
			billingZipText: $('.billing-zip-text'),
			billingZip: $('#billing-zip'),
			donationTotal: $('.js-donation-total'),
			paymentTotal: $('.js-payment-total'),
			donateButton: $('.js-donate'),
			makePurchase: $('.js-make-purchase'),
			makeFreePurchase: $('.js-make-free-purchase'),
			// Success messages
			defaultSuccessMsg: $('.js-default-success-msg'),
			verifySuccessMsg: $('.js-verification-success-msg'),
			declinedMsg: $('.js-declined-message'),
			donationReference: $('.js-donation-ref'),
			// Form
			forms: {
				donation: $('.js-main-donation-form') // todo: un-nest this once Donate JS updated to use same classes
			}
		};
	}

	setupParsley() {
		enableParsleyPostcodeValidator();
		enableParsleyBlocklistValidator();
		enableParsleyNotEmailValidator();
		enableParsleyNotHtmlValidator();

		// Bind form validation
		this.ui.forms.donation.parsley({
			excluded: ':disabled',
			errorsContainer: function(field) {
				const $el = field.$element;
				return $el.closest('.form-group').find('.error-container');
			}
		});

		// Scroll to first form error
		this.ui.forms.donation.parsley().on('form:error', (e) => {
			scrollToParsleyError(e, ".js-step.show", ".box__content");
		});
	}

	setupAddressUtils() {
		enableTomSelects([
			"#country", 
			"#billing-country",
		]);

		enableLoqate([
			"#giftaid-loqate"
		]); // Needs to be after enableTomSelects

		enablePostcodeLabelToggle();

		new AutoPopulateAddress({
			inputSources: '.js-giftaid-address-fields-container input, .js-giftaid-address-fields-container select',
			textTarget: '.js-autopopulated-address',
			textTargetContainer: $(".js-autopopulated-address-container"),
			stepToTriggerPopulate: $("#js-step-review"),
			editButton: $('.js-manual-billing-address'),
		});
	}

	bindEventHandlers() {
		// Next and prev buttons
		this.ui.buttonNext.on('click', this.goToNextStep.bind(this));
		this.ui.buttonPrev.on('click', this.goToPrevStep.bind(this));

		// Amount screen
		this.ui.amountField.on('change', this.updateDonationTotal.bind(this));

		// Details screen
		this.ui.addressContainer.on('show.bs.collapse', this.onAddressContainerShown.bind(this));

		// Other
		this.ui.forms.donation.on('keydown', disableSubmittingFormWithEnterKey.bind(this));

		// Recaptcha
		this.recaptcha = new RecaptchaUtils();
	}

	async initStripe() {
		console.log("TicketingForm.initStripe", "Start");

		this.stripeUtils = new StripeUtils({
			onSuccess: this.showThankyouScreen.bind(this),
			onFail: this.showDeclinedScreen.bind(this),
			confirmEndpoint: "/purchase/confirm",
		});

		try {
			this.stripe = await this.stripeUtils.loadStripe();
		} catch(e) {
			showErrorBar();
			this.ui.buttonNext.off("click");
			return;
		}

		// CardElement integration
		this.cardElements = new StripeCardElements(this, {
			onUnmount: () => {
				this.disablePurchaseButton();
				this.recaptcha.reset();
			},
			onReset: () => {
				this.enablePurchaseButton();
				this.recaptcha.reset();
			},
			onSuccess: this.onSuccessfulPurchase.bind(this),
		});

		// Referenced by StripeExpressElements
		// TODO: when we update Donate JS to use StripeCardElements, remove this line and pass callback as an arg to StripeExpressElements instead
		this.submitPayment = this.cardElements.submitPayment.bind(this.cardElements);

		// Express Checkout integration
		this.expressElements = new StripeExpressElements(this, {
			appleBtnText: 'buy',
			googleBtnText: 'pay',
		});

		this.enablePurchaseButton();
		this.paymentMethods.setCardElements(this.cardElements);
		this.paymentMethods.setExpressElements(this.expressElements);

		console.log("TicketingForm.initStripe", "Done");
	}

	//
	// Event handler functions
	//

	onUpdateBasket(basketData) {
		console.log("TicketingForm.onUpdateBasket", basketData);

		const urlParams = new URLSearchParams(window.location.search);
		const ticketingPageId = urlParams.get('ticketingPageId');
		let groupedTicketingPage = basketData.groupedTicketingPages.find(gtp => gtp.ticketingPage.id == ticketingPageId);

		if(!groupedTicketingPage) {
			groupedTicketingPage = {
				totalPrice: 0, 
				count: 0,
			}
		}

		this.ui.amountField.val(groupedTicketingPage.totalPrice).trigger('change');
		this.ui.numTicketsText.html(groupedTicketingPage.count + " ticket" + (groupedTicketingPage.count > 1 ? "s" : ""));
		this.ui.numTicketsField.val(groupedTicketingPage.count);
	}

	onTimerReset() {
		console.log("TicketingForm.onTimerReset");
		this.steps.goToStep('basket');
	}

	onAddressContainerShown() {
		console.log("TicketingForm.onAddressContainerShown")
		this.ui.forms.donation.parsley().reset();
		$("#giftaid-address-shown").val("true"); // Shares HTML with the Donate Form hence "GiftAid"
	}

	// 
	// Core functions
	// 

	isFormValid(currentStep) {
		const parsleyArgs = currentStep ? { group: 'step-' + currentStep } : {};
		this.ui.forms.donation.parsley().validate(parsleyArgs);
		return this.ui.forms.donation.parsley().isValid(parsleyArgs);
	}

	goToNextStep(e) {
		e.preventDefault();

		const currentStep = this.steps.getCurrent();
		const nextStep = this.steps.getNext();

		console.log("TicketingForm.goToNextStep", currentStep, nextStep);

		if(this.isFormValid(currentStep)) {
			if(nextStep == STEP_DETAILS) {
				// Check Express Elements Checkout payment methods - PayPal is only enabled if over £5
				// This isn't included in getAvailablePaymentMethods because it can take 1-2s to init and mount
				this.expressElements.checkMountedPaymentMethods(this.ui.amountField.val());
			}

			// Check which payment methods are available so we can show/hide as required
			// If only card is available then skip the screen and go straight to the card payment screen
			if(nextStep === STEP_REVIEW) {
				const availablePaymentMethods = this.paymentMethods.getAvailable();
				this.steps.toggleStepVisibility(STEP_PAYMENT_METHOD, Object.keys(availablePaymentMethods).length > 1);
				this.paymentMethods.showAvailable();
			}

			this.steps.goToStep(nextStep);
		} else { // This is here as we trigger this function when the user clicks browser forward button
			this.steps.goToStep(currentStep);
		}
	}

	goToPrevStep(e) {
		e.preventDefault();

		const currentStep = this.steps.getCurrent();
		const prevStep = this.steps.getPrev();

		console.log("TicketingForm.goToPrevStep", currentStep, prevStep);

		this.steps.goToStep(prevStep);
	}

	showThankyouScreen() {
		console.log("TicketingForm.showThankyouScreen");

		// Show a slightly different success messages if the user has to verify their large donation.
		let amount = parseFloat(this.ui.amountField.val()) || 0;
		
		if(amount >= this.verificationThreshold) {
			this.ui.defaultSuccessMsg.hide();
			this.ui.verifySuccessMsg.show();
		}

		this.steps.goToStep('success');
		this.basket.timer.hide();

		// Analytics
		AnalyticsEvent.facebook('Donate');
		AnalyticsEvent.heap('Donation', {donationValue:amount});
		AnalyticsEvent.gtm('DonationSuccess', {donationValue: amount});
	}

	// Response can be a few things:
	// - Our API response (DonationAjaxResponse)
	// - Stripe's Error object: https://docs.stripe.com/api/errors
	// - A native JS error
	showDeclinedScreen(response) {
		console.log("TicketingForm.showDeclinedScreen", response);

		// We only want to surface human-readable messages, not native JS error messages, so we need to filter those out.
		let errorMsg = "There was a problem making the purchase."

		if(response.stripeErrorMsg) {
			// Our API response
			errorMsg = response.stripeErrorMsg;
		} else if(response.type && response.message) {
			// Stripe Error object (native JS objects have a 'message' property, so we can't blindly use response.message)
			errorMsg = response.message;
		}

		this.ui.declinedMsg.text(errorMsg);

		// If there is a donation id, add it to the error message.
		if(response.donationId) {
			var donationRefString = this.ui.donationReference;
			var prefix = $(donationRefString).data('prefix') || '';
			donationRefString.text(prefix + response.donationId);
		}

		this.steps.goToStep('failed');
		this.basket.timer.hide();

		// Analytics
		AnalyticsEvent.gtm('DonationFailure', {conversionValue: this.ui.amountField.val()});
		AnalyticsEvent.heap('DonationFailure', {donationValue: this.ui.amountField.val()});
	}

	// Update the various on-screen amounts, plus the final payment value passed to Stripe
	updateDonationTotal() {
		const donationValue = parseFloat(this.ui.amountField.val()) || 0;

		// Fee percentage may vary depending on amount
		this.calculateFeePercentage(donationValue);

		const feeValue = donationValue * this.fees;
		const tipValue = donationValue * this.tipPerc;
		const feesCovered = this.ui.coverFeesField.val() == "true";
		const paymentValue = donationValue + (feesCovered * feeValue) + tipValue; // Payment value might have fees/tip added which is separate to the donation
		const paymentValueInPence = parseInt((paymentValue * 100).toFixed(0)); // Stripe needs a whole number in pence

		// Update the normal donation total
		updateAmountText(this.ui.donationTotal, donationValue);
		
		// Update the fees total
		updateAmountText(this.ui.feeAmount, feeValue);

		// Update the tips total
		updateAmountText(this.ui.tipAmount, tipValue);
		this.ui.tipAmountField.val(tipValue.toFixed(2));

		// Update payment total (donation + fee + tip)
		updateAmountText(this.ui.paymentTotal, paymentValue);

		// Set the payment amount on the ExpressElements, if we don't do this, the donation amount stays at what it was set to originally.
		if(this.expressElements) {
			this.expressElements.updateAmount(paymentValueInPence);
		}

		// Show the phone input + make it required if this donation is over the threshold or if the charity has chosen for it to be required
		togglePhoneInput(donationValue);

		// Show/hide buttons on the review screen - if a free event then show 'Complete purchase' button so they can just save without using Stripe
		this.ui.reviewNextContainer.toggle(donationValue > 0);
		this.ui.reviewCompleteContainer.toggle(donationValue == 0);

		// Also show/hide the tip slider, if a free event then no need to add a tip (the `?.` is in case it hasn't been initated yet)
		this.feesSlider?.ui.container.toggle(donationValue > 0);

		// Toggle the steps based on if free/paid tickets
		this.steps.toggleStepVisibility(STEP_PAYMENT_METHOD, donationValue > 0);
		this.steps.toggleStepVisibility(STEP_CARD_PAYMENT, donationValue > 0);
	}

	onClickPurchaseButton(e) {
		console.log("TicketingForm.onClickPurchaseButton");
		e.preventDefault();

		this.disablePurchaseButton();

		// If not valid, don't bother generating a recaptcha token as it could've expired by the time they re-submit after
		// fixing any errors.
		if(!this.isFormValid()) {
			this.enablePurchaseButton();
			return;
		}

		this.executeRecaptcha();
	}

	// If it's a free purchase, the main difference at this stage is just validation
	// We don't need the billing card info etc so are just validating the current step (review)
	onClickFreePurchaseButton(e) {
		console.log("TicketingForm.onClickPurchaseButton");
		e.preventDefault();

		const currentStep = this.steps.getCurrent();

		if(!this.isFormValid(currentStep)) {
			this.enablePurchaseButton();
			return;
		}

		this.executeRecaptcha();
	}

	executeRecaptcha() {
		grecaptcha.execute().then(() => {
			// this.onRecaptchaTokenGenerated() gets called after execute() finishes, which is where the Stripe token generation
			// and other bits happen. Inside donate/recaptcha.vm is where you define what happens after a token has been created.
		}).catch(() => {
			this.enablePurchaseButton();
		});
	}

	// This gets called when the recaptcha token has been generated. Triggered by attempting a payment element/card donation
	onRecaptchaTokenGenerated(token) {
		console.log("TicketingForm.onRecaptchaTokenGenerated", token);
		console.log('Used payment method: ', this.paymentMethods.getUsedPaymentMethod());
		this.recaptcha.setToken(token);

		const donationValue = parseFloat(this.ui.amountField.val()) || 0;

		if(donationValue == 0) {
			// Bypass Stripe as no payment to take
			this.cardElements.submitPayment();
			return;
		}

		// Card payments
		this.cardElements.handlePayment();
	}

	onSuccessfulPurchase(response) {
		console.log("TicketingForm.onSuccessfulPurchase", response);
		$(".js-signup-btn").attr("href", "/join?key=" + (response.donationSignupKey || ''));
	}

	//
	// Helper functions
	//

	disablePurchaseButton() {
		console.log("TicketingForm.disablePurchaseButton");
		this.ui.makePurchase.addClass('disabled');
		this.ui.makeFreePurchase.addClass('disabled');
		this.ui.forms.donation.off("submit");
		this.ui.makePurchase.off("click");
		this.ui.makeFreePurchase.off("click");
		this.ui.buttonPrev.addClass('disabled');
		this.ui.buttonPrev.off("click");
	}

	enablePurchaseButton () {
		console.log("TicketingForm.enablePurchaseButton");
		this.ui.makePurchase.removeClass('disabled');
		this.ui.makeFreePurchase.removeClass('disabled');
		this.ui.buttonPrev.removeClass('disabled');
		// Bind both form submit and button press to the click handler. This ensures form submits via AJAX
		// e.g. enter key presses are handled appropriately, rather than the default action, which is to do a POST page load
		this.ui.forms.donation.on("submit", this.onClickPurchaseButton.bind(this));
		this.ui.makePurchase.on("click", this.onClickPurchaseButton.bind(this));
		this.ui.makeFreePurchase.on("click", this.onClickFreePurchaseButton.bind(this));
		this.ui.buttonPrev.off("click").on('click', this.goToPrevStep.bind(this));
	}

	// Pass in either a % or a fixed amount, then use donation amount to work out the other one
	setTip(tipPercentage, tipAmount) {
		const donationValue = this.ui.amountField.val() || 0;
		tipPercentage = tipPercentage || (tipAmount / donationValue).toFixed(4);
		tipAmount = tipAmount || (tipPercentage * donationValue).toFixed(2);

		console.log("TicketingForm.setTip", tipPercentage, tipAmount);

		this.tipPerc = tipPercentage;
		this.ui.tipField.val(tipPercentage);
		this.ui.tipAmountField.val(tipAmount);

		const tipPercentFormatted = this.tipPerc.toLocaleString('en-GB', {
			style: 'percent', 
			minimumFractionDigits:0, 
			maximumFractionDigits:2
		});

		this.ui.tipPerc.html(tipPercentFormatted);

		// Trigger amounts to update
		this.updateDonationTotal()
	}

	calculateFeePercentage(donationValue) {
		console.log("TicketingForm.calculateFeePercentage", donationValue);
		if(donationValue == 0) return;

		const feeField = this.ui.feeField;
		const feeText = $(".js-fee-perc");

		// Start with the default fee
		this.fees = Number(feeField.data('default'));

		// Check if above the fee thresholds, if so update the fee
		let activeThreshold = 0;
		this.ui.feeThresholds.each((i, el) => {
			const threshold = Number(el.dataset.threshold);

			if(donationValue >= threshold && threshold > activeThreshold) {
				activeThreshold = threshold;
				this.fees = Number(el.value);
			}
		});

		const feeFormatted = this.fees.toLocaleString('en-GB', {
			style: 'percent', 
			minimumFractionDigits:0, 
			maximumFractionDigits:2
		});

		feeField.val(this.fees);
		feeText.html(feeFormatted);

		console.log("TicketingForm.calculateFeePercentage", "Fee set to " + feeFormatted);
	}
}

// See comment at top of file for why this is here.
if($('[data-module-group="ticketing-form"]').length > 0) {
	window.donateInstance = new TicketingForm();
}